import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Progress,
  FormFeedback,
} from "reactstrap";
import ApiService from "../../services/ApiService";

const SendFax = () => {
  const [faxData, setFaxData] = useState({
    toNumber: "",
    fromNumber: "",
    file: null,
  });
  const [faxNumbers, setFaxNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingNumbers, setLoadingNumbers] = useState(true);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});

  // E164 format validation function
  const isValidE164 = (number) => {
    // E164 format: +[country code][number] (1-15 digits total after +)
    const e164Regex = /^\+[1-9]\d{1,14}$/;
    return e164Regex.test(number);
  };

  const validateField = useCallback(
    (name, value) => {
      setValidationErrors((prevErrors) => {
        const errors = { ...prevErrors };

        switch (name) {
          case "toNumber":
            if (!value.trim()) {
              errors.toNumber = "Recipient fax number is required";
            } else if (!isValidE164(value.trim())) {
              errors.toNumber =
                "Fax number must be in E164 format (e.g., +1234567890)";
            } else {
              delete errors.toNumber;
            }
            break;
          case "fromNumber":
            if (!value.trim()) {
              errors.fromNumber = "From number is required";
            } else {
              delete errors.fromNumber;
            }
            break;
          case "file":
            if (!value) {
              errors.file = "A file is required to send a fax";
            } else {
              delete errors.file;
            }
            break;
          default:
            break;
        }

        return errors;
      });
    },
    [] // No dependencies - uses functional state update
  );

  // Fetch available fax numbers on component mount
  useEffect(() => {
    const fetchFaxNumbers = async () => {
      try {
        setLoadingNumbers(true);
        const numbers = await ApiService.getFaxNumbers();
        setFaxNumbers(Array.isArray(numbers) ? numbers : []);

        // Auto-select the first number if only one is available
        if (numbers && numbers.length === 1) {
          const selectedNumber =
            numbers[0].fromNumber || numbers[0].number || numbers[0];
          setFaxData((prev) => ({
            ...prev,
            fromNumber: selectedNumber,
          }));
        }
      } catch (err) {
        console.error("Failed to fetch fax numbers:", err);
        setFaxNumbers([]);
      } finally {
        setLoadingNumbers(false);
      }
    };

    fetchFaxNumbers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFaxData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear success/error messages when user starts typing
    if (success) setSuccess("");
    if (error) setError("");

    // Validate field in real-time
    validateField(name, value);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type and size
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/tiff",
      ];
      const maxSize = 10 * 1024 * 1024; // 10MB

      if (!allowedTypes.includes(file.type)) {
        setError("Please select a PDF, JPEG, PNG, or TIFF file");
        setValidationErrors((prev) => ({ ...prev, file: "Invalid file type" }));
        return;
      }

      if (file.size > maxSize) {
        setError("File size must be less than 10MB");
        setValidationErrors((prev) => ({ ...prev, file: "File too large" }));
        return;
      }

      setFaxData((prev) => ({
        ...prev,
        file: file,
      }));
      setError("");

      // Validate file field
      validateField("file", file);
    } else {
      // No file selected
      setFaxData((prev) => ({
        ...prev,
        file: null,
      }));
      validateField("file", null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");
    setUploadProgress(0);

    // Comprehensive validation
    const errors = {};

    // Validate recipient fax number
    if (!faxData.toNumber.trim()) {
      errors.toNumber = "Recipient fax number is required";
    } else if (!isValidE164(faxData.toNumber.trim())) {
      errors.toNumber = "Fax number must be in E164 format (e.g., +1234567890)";
    }

    // Validate from number
    if (!faxData.fromNumber.trim()) {
      errors.fromNumber = "From number is required";
    }

    // Validate file
    if (!faxData.file) {
      errors.file = "A file is required to send a fax";
    }

    // If there are validation errors, stop submission
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setError("Please fix the validation errors before submitting");
      setLoading(false);
      return;
    }

    // Clear any previous validation errors
    setValidationErrors({});

    try {
      const result = await ApiService.sendFax(faxData);
      setSuccess(
        `Fax sent successfully! Job ID: ${result.jobId || result.id || "N/A"}`
      );

      // Reset form
      setFaxData({
        toNumber: "",
        fromNumber:
          faxNumbers.length === 1
            ? faxNumbers[0].fromNumber || faxNumbers[0].number || faxNumbers[0]
            : "",
        file: null,
      });
      // Reset file input
      document.getElementById("fileInput").value = "";
    } catch (err) {
      setError(err.message || "Failed to send fax");
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="app-main__inner">
      <div className="app-page-title">
        <div className="page-title-wrapper">
          <div className="page-title-heading">
            <div className="page-title-icon">
              <i className="pe-7s-paper-plane icon-gradient bg-mean-fruit"></i>
            </div>
            <div>
              Send Fax
              <div className="page-title-subheading">
                Send fax documents to recipients
              </div>
            </div>
          </div>
        </div>
      </div>

      <Row>
        <Col md="12">
          <Card className="main-card mb-3">
            <CardHeader>
              <i className="header-icon pe-7s-mail icon-gradient bg-plum-plate"></i>
              Fax Details
            </CardHeader>
            <CardBody>
              {success && <Alert color="success">{success}</Alert>}
              {error && <Alert color="danger">{error}</Alert>}

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label for="toNumber">Recipient Fax Number *</Label>
                      <Input
                        type="tel"
                        name="toNumber"
                        id="toNumber"
                        placeholder="+1234567890"
                        value={faxData.toNumber}
                        onChange={handleInputChange}
                        required
                        invalid={!!validationErrors.toNumber}
                      />
                      {validationErrors.toNumber && (
                        <FormFeedback>{validationErrors.toNumber}</FormFeedback>
                      )}
                      <small className="form-text text-muted">
                        Must be in E164 format (e.g., +1234567890)
                      </small>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="fromNumber">From Number *</Label>
                      {loadingNumbers ? (
                        <Input disabled placeholder="Loading fax numbers..." />
                      ) : faxNumbers.length === 0 ? (
                        <Input
                          disabled
                          placeholder="No fax numbers available"
                        />
                      ) : (
                        <Input
                          type="select"
                          name="fromNumber"
                          id="fromNumber"
                          value={faxData.fromNumber}
                          onChange={handleInputChange}
                          required
                          invalid={!!validationErrors.fromNumber}
                        >
                          <option value="">Select a from number</option>
                          {faxNumbers.map((number, index) => (
                            <option
                              key={index}
                              value={
                                number.fromNumber || number.number || number
                              }
                            >
                              {number.fromNumber || number.number || number}
                              {number.description && ` - ${number.description}`}
                            </option>
                          ))}
                        </Input>
                      )}
                      {validationErrors.fromNumber && (
                        <FormFeedback>
                          {validationErrors.fromNumber}
                        </FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label for="fileInput">PDF File *</Label>
                      <Input
                        type="file"
                        name="file"
                        id="fileInput"
                        accept=".pdf,.jpg,.jpeg,.png,.tiff"
                        onChange={handleFileChange}
                        required
                        invalid={!!validationErrors.file}
                      />
                      {validationErrors.file && (
                        <FormFeedback>{validationErrors.file}</FormFeedback>
                      )}
                      <small className="form-text text-muted">
                        Supported formats: PDF Max size: 10MB
                      </small>
                    </FormGroup>
                  </Col>
                </Row>

                {uploadProgress > 0 && (
                  <FormGroup>
                    <Label>Upload Progress</Label>
                    <Progress value={uploadProgress} />
                  </FormGroup>
                )}

                <div className="text-center">
                  <Button
                    color="primary"
                    size="lg"
                    type="submit"
                    style={{ color: "white" }}
                    disabled={
                      loading ||
                      !faxData.toNumber ||
                      !faxData.fromNumber ||
                      !faxData.file ||
                      Object.keys(validationErrors).length > 0 ||
                      !isValidE164(faxData.toNumber)
                    }
                  >
                    {loading ? "Sending Fax..." : "Send Fax"}
                  </Button>
                  {Object.keys(validationErrors).length > 0 && (
                    <div className="mt-2">
                      <small className="text-danger">
                        Please fix validation errors before submitting
                      </small>
                    </div>
                  )}
                </div>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SendFax;
